/** @format */

import { useEffect } from "react";
import {
  BookOpen,
  ClipboardCheck,
  GitCompare,
  LineChart,
  Settings,
  TestTube,
  Sparkles,
} from "lucide-react";
import ActionCard from "../../components/ui/ActionCard";
import HeroSection from "../../components/ui/HeroSection";
import { track } from "../../lib/posthog";
import {
  FLAG_RMA_ANALYSIS,
  FLAG_TEST_PROCEDURES,
  FLAG_TRACK_CHANGES,
  useFeatureFlagEnabled,
} from "../../lib/featureFlags";

type ActiveFeature =
  | "none"
  | "generateSOP"
  | "generateBOM"
  | "trackChanges"
  | "qualityControl"
  | "testProcedures"
  | "analyzeProcess";

const featureLabels: Record<ActiveFeature, string> = {
  none: "Select an action",
  generateSOP: "Standard Operating Procedure (SOP)",
  generateBOM: "Bill of Materials (BOM)",
  trackChanges: "Track Changes",
  qualityControl: "Quality Control",
  testProcedures: "Test Procedures",
  analyzeProcess: "Analyze Processes",
};

const featureDescriptions: Record<ActiveFeature, string> = {
  none: "",
  generateSOP:
    "Create step-by-step procedures for manufacturing processes",
  generateBOM: "Generate detailed bills of materials for your products",
  trackChanges: "Monitor and manage version control for your documentation",
  qualityControl: "Implement quality assurance protocols and checklists",
  testProcedures: "Develop testing protocols and validation procedures",
  analyzeProcess: "Analyze and optimize your manufacturing workflows",
};

export default function MainContent() {
  // WORKAROUND: see lib/WORKAROUND_feature_flags.md
  const testProceduresEnabled = useFeatureFlagEnabled(FLAG_TEST_PROCEDURES);
  const rmaEnabled = useFeatureFlagEnabled(FLAG_RMA_ANALYSIS);
  const trackChangesEnabled = useFeatureFlagEnabled(FLAG_TRACK_CHANGES);

  useEffect(() => {
    track("dashboard_viewed", { tab_name: "home" });
  }, []);

  return (
    <div className="min-h-screen pt-20 overflow-x-hidden">
      <main className="pt-8 w-full mx-auto px-6 lg:px-10">
        <div className="max-w-[1440px] mx-auto">
          {/* Hero Section */}
          <HeroSection
            title="What can I help you launch?"
            description="Transform your manufacturing processes with AI-powered tools"
            icons={[
              <Sparkles className="w-8 h-8 text-[#3C6AC6]" />,
              <Sparkles className="w-8 h-8 text-[#FB6B96]" />,
            ]}
          />

          {/* Active Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            <ActionCard
              icon={BookOpen}
              title={featureLabels.generateSOP}
              description={featureDescriptions.generateSOP}
              href="/generate-sop"
              variant="primary"
            />

            <ActionCard
              icon={TestTube}
              title={featureLabels.testProcedures}
              description={featureDescriptions.testProcedures}
              href="/test-procedures"
              variant="primary"
              disabledReason={testProceduresEnabled === false ? "Not available on your current plan" : undefined}
            />

            <ActionCard
              icon={GitCompare}
              title={featureLabels.trackChanges}
              description={featureDescriptions.trackChanges}
              href="/track-changes"
              variant="primary"
              disabledReason={trackChangesEnabled === false ? "Not available on your current plan" : undefined}
            />

            <ActionCard
              icon={ClipboardCheck}
              title={featureLabels.qualityControl}
              description="Open the RMA dashboard to analyze defects and manage datasets"
              href="/rma-dashboard"
              variant="primary"
              disabledReason={rmaEnabled === false ? "Not available on your current plan" : undefined}
            />
          </div>

          {/* Coming Soon Section */}
          <div className="mt-16">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
              Coming Soon
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: Settings, label: featureLabels.generateBOM, desc: featureDescriptions.generateBOM },
                { icon: LineChart, label: featureLabels.analyzeProcess, desc: featureDescriptions.analyzeProcess },
              ].map(({ icon: Icon, label, desc }) => (
                <div
                  key={label}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-800/20 bg-gray-900/20 opacity-60"
                >
                  <div className="w-9 h-9 rounded-lg bg-gray-800/40 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-400 truncate">{label}</p>
                    <p className="text-xs text-slate-600 truncate">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
